import { apiRequest } from '@/lib/api';
import type {
  CourseKeyConfig,
  CourseKeyContentTabs,
  CourseKeyContentStructured,
  CourseKeyPlatform,
  CourseKeyStatus,
  IspringContentItem,
  CourseKeyEnrollmentRow,
  MindtapEnrollmentPreview,
} from '@/types/course-key';

function buildQuery(params: Record<string, string | number | undefined | null>) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    qs.set(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

const BASE = '/api/admin/course-keys';

export type CourseKeyListQuery = {
  page?: number;
  limit?: number;
  platform?: CourseKeyPlatform;
  status?: CourseKeyStatus;
  categoryId?: string;
  programId?: string;
  search?: string;
};

export type CourseKeyPayload = {
  platform: CourseKeyPlatform;
  status?: CourseKeyStatus;
  categoryId: string;
  programId: string;
  deliveryModes: string[];
  ispringCourseIds?: string[];
  rulesHtml?: string;
  instructionsHtml?: string;
  contentTabs?: Partial<CourseKeyContentTabs>;
  contentStructured?: Partial<CourseKeyContentStructured>;
  instructionFiles?: Array<{
    fileName: string;
    fileUrl: string;
    fileType?: string;
    fileSize?: number;
  }>;
  credentialsPlaceholder?: 'inline' | 'tab_block';
  durationValue: number;
  durationUnit: 'days' | 'weeks';
  extensionFees?: { value: number; unit: 'days' | 'weeks'; amountCents: number }[];
  reactivationFee: {
    inactiveValue: number;
    inactiveUnit: 'days' | 'weeks';
    accessValue: number;
    accessUnit: 'days' | 'weeks';
    amountCents: number;
  };
  portalUrl?: string;
  sharedKeysEnabled?: boolean;
  sharedKeys?: Array<{ name: string; courseKey: string }>;
};

export const courseKeysApi = {
  list: async (query: CourseKeyListQuery = {}) => {
    const res = await apiRequest<{ data: { items: CourseKeyConfig[]; total: number; page: number; limit: number } }>(
      `${BASE}${buildQuery(query)}`
    );
    return res.data;
  },

  getById: async (id: string) => {
    const res = await apiRequest<{ data: CourseKeyConfig }>(`${BASE}/${id}`);
    return res.data;
  },

  create: async (payload: CourseKeyPayload) => {
    const res = await apiRequest<{ data: CourseKeyConfig }>(BASE, {
      method: 'POST',
      body: payload,
    });
    return res.data;
  },

  update: async (id: string, payload: Partial<CourseKeyPayload>) => {
    const res = await apiRequest<{ data: CourseKeyConfig }>(`${BASE}/${id}`, {
      method: 'PUT',
      body: payload,
    });
    return res.data;
  },

  updateStatus: async (id: string, status: CourseKeyStatus) => {
    const res = await apiRequest<{ data: CourseKeyConfig }>(`${BASE}/${id}/status`, {
      method: 'PATCH',
      body: { status },
    });
    return res.data;
  },

  listIspringContents: async (pageSize = 200, pageToken?: string) => {
    const res = await apiRequest<{
      data: { items: IspringContentItem[]; nextPageToken?: string };
    }>(`${BASE}/ispring/contents${buildQuery({ pageSize, pageToken })}`);
    return res.data;
  },

  getProgramsByCategory: async (categoryId: string) => {
    const res = await apiRequest<{
      data: Array<{ _id: string; title: string; platform?: { isIspring?: boolean } }>;
    }>(`${BASE}/programs-by-category/${categoryId}`);
    return res.data;
  },

  listConfigEnrollments: async (configId: string) => {
    const res = await apiRequest<{ data: CourseKeyEnrollmentRow[] }>(
      `${BASE}/${encodeURIComponent(configId)}/enrollments`
    );
    return res.data;
  },

  previewMindtapEnrollment: async (
    configId: string,
    orderId: string,
    includeKey = false
  ) => {
    const res = await apiRequest<{ data: MindtapEnrollmentPreview }>(
      `${BASE}/${encodeURIComponent(configId)}/enrollments/${encodeURIComponent(orderId)}/preview${includeKey ? '?includeKey=true' : ''}`
    );
    return res.data;
  },

  releaseMindtapEnrollment: async (
    configId: string,
    orderId: string,
    keys: Array<{ name: string; courseKey: string }>
  ) => {
    const res = await apiRequest<{
      data: {
        orderId: string;
        releaseStatus: string;
        releasedAt: string;
        releasedKeyCount: number;
      };
    }>(
      `${BASE}/${encodeURIComponent(configId)}/enrollments/${encodeURIComponent(orderId)}/release`,
      { method: 'POST', body: { keys } }
    );
    return res.data;
  },

  setMindtapEnrollmentVisibility: async (
    configId: string,
    orderId: string,
    credentialsHidden: boolean
  ) => {
    const res = await apiRequest<{
      data: { orderId: string; credentialsHidden: boolean };
    }>(
      `${BASE}/${encodeURIComponent(configId)}/enrollments/${encodeURIComponent(orderId)}/visibility`,
      { method: 'PATCH', body: { credentialsHidden } }
    );
    return res.data;
  },

  resendLoginInstructions: async (configId: string, orderId: string) => {
    const res = await apiRequest<{
      data: { orderId: string; email: string; programName: string };
    }>(
      `${BASE}/${encodeURIComponent(configId)}/enrollments/${encodeURIComponent(orderId)}/resend-login-instructions`,
      { method: 'POST', body: {} }
    );
    return res.data;
  },

  applyPaidIspringExtension: async (configId: string, orderId: string) => {
    const res = await apiRequest<{
      data: {
        newDeactivationDate: string;
        value: number;
        unit: 'days' | 'weeks';
        emailSent?: boolean;
      };
    }>(
      `${BASE}/${encodeURIComponent(configId)}/enrollments/${encodeURIComponent(orderId)}/apply-paid-extension`,
      { method: 'POST', body: {} }
    );
    return res.data;
  },

  presignInstructionFileUpload: async (body: {
    fileName: string;
    fileType: string;
    fileSize: number;
  }) => {
    const res = await apiRequest<{
      data: { uploadUrl: string; fileUrl: string; expiresIn: number };
    }>(`${BASE}/instruction-files/upload-url`, {
      method: 'POST',
      body,
    });
    return res.data;
  },
};
